import React from 'react';
import styled from 'astroturf/react';

import { Body } from 'js/design_system/Body';
import { BodyLink } from 'js/design_system/link/BodyLink';
import { Props as ImpressionProps } from 'js/utils/impressions/ImpressionWatcher';
import { ImpressionWatcher } from 'js/utils/impressions/ImpressionWatcher';

import {
  Paper,
  OpenalexWork,
  Author,
  PaperUpdateUnlinked,
} from 'js/social/shared/types';
import { NoThumbnailIcon } from 'js/social/shared/Icons';
import { TruncateHeight } from 'js/social/shared/SocialEditor/TruncateHeight';
import { Attachment } from 'js/liswp/build_vu/Attachment';
import { SimplePaperPodcast } from 'js/podcast_ai/SimplePaperPodcast';
import { RecommendedPodcastBadge } from 'js/podcast_ai/RecommendedPodcastBadge';

export type PaperSize = 'xsmall' | 'small' | 'medium' | 'large';

const LINE_HEIGHT = 20;

const PaperThumbnail = styled.div<{
  size?: 'sm' | 'md';
  variant?: 'default' | 'dense' | 'no_url' | 'library' | 'library-recommended';
}>`
  // Layout
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex-shrink: 0;
  cursor: pointer;

  img,
  svg {
    width: 48px;
    height: 68.87px;
    max-width: 100%;
    max-height: 100%;
  }

  &.variant-dense {
    width: 44px;
    height: 62.63px;
  }

  img {
    border: 0.348px solid var(--neutrals-30, #ebebee);

    @include for-tablet-sm-up {
      border: 0.681px solid var(--neutrals-30, #ebebee);
    }
  }

  &.size-md {
    img,
    svg {
      @include for-tablet-sm-up {
        width: 94px;
        height: 134.87px;
      }
    }

    img {
      @include for-tablet-sm-up {
        border: 0.681px solid var(--neutrals-30, #ebebee);
      }
    }
  }
`;

const ContentAndButtonsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex: 1 0 0;
  gap: var(--spacing-xs-8, 8px);
  overflow: hidden;
`;

const ContentContainer = styled.div<{
  variant?: 'default' | 'dense' | 'no_url' | 'library' | 'library-recommended';
}>`
  display: flex;
  flex-direction: column;
  align-self: stretch;
  gap: var(--spacing-xs-8, 8px);
  padding-bottom: 8px;

  &.variant-dense {
    gap: 0;
    padding-bottom: 0;
  }
`;

const PaperTitle = styled(BodyLink)<{
  variant?: 'default' | 'dense' | 'no_url' | 'library' | 'library-recommended';
}>`
  color: var(--text-primary, #223);
  font-weight: 600;
  margin-bottom: 4px;
  display: inline-block;

  &:hover,
  &:active,
  &:focus {
    color: inherit;
  }

  &.variant-dense {
    margin-bottom: 0px;
    font-size: 14px;
  }
`;

const Abstract = styled(Body)`
  line-height: ${LINE_HEIGHT}px;
`;

const SeeMoreButton = styled(Abstract)<{
  variant?: 'default' | 'dense' | 'no_url' | 'library' | 'library-recommended';
}>`
  background: var(--feed-paper-card-inner-cntr-Color, #f2efe8);
  text-decoration: underline;

  @import 'stylesheets/design_system/font.scss';
  @include body__small;

  &.variant-dense {
    background: var(--feed-paper-card-inner-cntr-Color, #f9f7f4);
    @include breakpoint(small) {
      background: var(--feed-paper-card-inner-cntr-Color, #f2efe8);
    }
  }

  &:not(.variant-dense) {
    padding-right: 50px;

    @include for-tablet-sm-up {
      background: var(--feed-paper-card-inner-cntr-Color, #f9f7f4);
      padding-right: 100px;
    }

    @include for-tablet-up {
      padding-right: 150px;
    }
  }
`;

const Container = styled.div<{
  size?: 'sm' | 'md';
  variant?: 'default' | 'dense' | 'no_url' | 'library' | 'library-recommended';
}>`
  word-break: break-word;

  display: flex;
  align-items: flex-start;
  gap: var(--feed-paper-card-gap, 12px);
  align-self: stretch;

  background: var(--feed-paper-card-inner-cntr-Color, #f2efe8);
  padding: var(--spacing-sm-24, 24px) var(--spacing-xs-16, 16px)
    var(--spacing-sm-24, 24px) var(--spacing-xs-16, 16px);

  &.variant-library,
  &.variant-library-recommended {
    .podcast-player-wrapper > div:first-child {
      background: #fafafa;
      border: 1px solid #dddde2;
      border-radius: 4px;

      > div:first-child {
        padding: 16px 14px;
      }
    }
  }

  &.variant-library-recommended {
    background: transparent;
    padding: 0;
  }

  &.variant-dense {
    padding: var(--spacing-xs-12, 12px);
    border-radius: var(--border-radius-radius-md);
    cursor: pointer;
    &:hover {
      background: var(--background-light-blue, #eef2f9);
      ${PaperTitle} {
        text-decoration: underline;
      }

      ${SeeMoreButton} {
        background: var(--background-light-blue, #eef2f9);
      }
    }
  }

  @include for-tablet-sm-up {
    background: var(--feed-paper-card-inner-cntr-Color, #f9f7f4);
    padding: var(--spacing-sm-24, 24px) var(--spacing-sm-24, 24px)
      var(--spacing-sm-24, 24px) var(--spacing-sm-24, 24px);

    &.variant-dense {
      padding: var(--spacing-xs-12, 12px);
    }

    &.variant-library-recommended {
      background: transparent;
      padding: 0;
    }
  }

  &.size-sm {
    padding: var(--spacing-xs-16, 16px);
  }
`;

const AttachmentContainer = styled.div`
  margin-top: var(--spacing-xs-16);
  margin-bottom: -16px;
  aspect-ratio: 3/2;
  overflow: hidden;
  cursor: pointer;
`;

const PodcastPlayerWrapper = styled.div`
  width: 100%;
`;

const PaperAuthorsAndJournal = styled.div<{
  isSnippet?: boolean;
  variant?: 'default' | 'dense' | 'no_url' | 'library' | 'library-recommended';
}>`
  padding-bottom: 8px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;

  > p {
    align-self: stretch;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  a,
  span,
  div {
    color: var(--text-primary, #141413);
  }
  &.isSnippet {
    padding-bottom: 4px;
  }

  &.variant-dense {
    padding-bottom: 4px;
  }
`;

const AuthorLink = styled(BodyLink)`
  &:hover,
  &:active,
  &:focus {
    color: inherit;
  }
`;

const PaperAuthor = ({
  author,
  size,
}: {
  author: Author;
  size?: 'xs' | 'sm';
}) =>
  author.profileUrl ? (
    <AuthorLink
      href={author.profileUrl}
      target={'_blank'}
      size={size}
      onClick={(e) => e.stopPropagation()}
    >
      {author.displayName}
    </AuthorLink>
  ) : (
    <Body as="span" size={size}>
      {author.displayName}
    </Body>
  );

type Props = {
  work: Paper | OpenalexWork | PaperUpdateUnlinked;
  canUseReader: boolean;
  showReader: () => void;
  impressionPropsGen: (clickTarget?: string) => ImpressionProps;
  paperInteractions: React.ReactNode;
  size?: 'sm' | 'md';
  showSummary?: boolean;
  showPreview?: boolean;
  isSnippet?: boolean;
  variant?: 'default' | 'dense' | 'no_url' | 'library' | 'library-recommended';
  onPodcastReady?: () => void;
};

const PaperContainer: React.FunctionComponent<Props> = ({
  work,
  canUseReader,
  showReader,
  impressionPropsGen,
  paperInteractions,
  size = 'md',
  showSummary = true,
  showPreview = false,
  isSnippet = false,
  variant = 'default',
  onPodcastReady,
}) => {
  const { title, authors, publicationYear, publication, attachment, summary } =
    work;
  const url =
    variant === 'no_url' || variant === 'library-recommended'
      ? undefined
      : work.url;

  const publicationInfo: string[] = [];
  if (publicationYear) publicationInfo.push(publicationYear.toString());
  if (publication) publicationInfo.push(publication);

  const thumbnailImage =
    attachment?.thumbnailUrl != null ? (
      <img src={attachment.thumbnailUrl} alt="" />
    ) : (
      <NoThumbnailIcon />
    );

  let abstractHeightMultiplier = size === 'sm' ? 2 : 3;
  if (variant === 'dense') {
    // For mobile, show 2 lines in the abstract
    abstractHeightMultiplier = window.innerWidth > 567 ? 1 : 2;
  }

  const handleCardClick = (event?: React.MouseEvent) => {
    if (variant === 'dense') {
      canUseReader ? showReader() : window.open(url, '_blank');
    }

    // Used in Podcast Library
    if (
      variant === 'no_url' ||
      variant === 'library' ||
      variant === 'library-recommended'
    ) {
      if (!onPodcastReady) return;
      // Ignore click on text/"Read more" or author names
      if (event?.target instanceof Element) {
        const clickedElement = event.target as Element;
        if (
          clickedElement.closest('a[href*="/profile/"]') ||
          clickedElement.closest('[class*="TruncateHeight"]')
        ) {
          return;
        }
      }
      // Play the podcast instead
      SimplePaperPodcast.play(work.podcast?.workId);
    }
  };

  const content = (
    <>
      {showPreview && attachment ? null : canUseReader ? (
        <PaperThumbnail onClick={showReader} size={size} variant={variant}>
          {thumbnailImage}
        </PaperThumbnail>
      ) : (
        <PaperThumbnail
          as="a"
          href={url}
          target={'_blank'}
          size={size}
          variant={variant}
        >
          {thumbnailImage}
        </PaperThumbnail>
      )}
      <ContentAndButtonsContainer onClick={handleCardClick}>
        <ContentContainer
          data-coachmark-step="abstract-read-more"
          variant={variant}
        >
          {variant === 'library-recommended' && <RecommendedPodcastBadge />}
          <ImpressionWatcher
            stopPropagation={false}
            {...impressionPropsGen('work-card')}
          >
            {canUseReader ? (
              <PaperTitle size={size} onClick={showReader} variant={variant}>
                {title}
              </PaperTitle>
            ) : (
              <PaperTitle
                size={size}
                href={url}
                target={'_blank'}
                variant={variant}
              >
                {title}
              </PaperTitle>
            )}
            <PaperAuthorsAndJournal isSnippet={isSnippet} variant={variant}>
              {authors?.length > 0 && (
                <Body size={'sm'}>
                  by{' '}
                  {authors
                    .map((a) => (
                      <PaperAuthor
                        key={`${a.userId}:${a.displayName}`}
                        author={a}
                        size={'sm'}
                      />
                    ))
                    .flatMap((el) => [el, ', '])
                    .slice(0, -1)}
                </Body>
              )}
              {publicationInfo.length > 0 && (
                <Body size={'sm'}> {publicationInfo.join(', ')} </Body>
              )}
            </PaperAuthorsAndJournal>
            {showSummary && summary && (
              <TruncateHeight
                height={LINE_HEIGHT * abstractHeightMultiplier}
                readMoreImpressionProps={impressionPropsGen('see-more')}
                styledMoreElement={<SeeMoreButton variant={variant} />}
                verb="Read"
              >
                <Abstract size={'sm'}> Abstract: {summary} </Abstract>
              </TruncateHeight>
            )}
            {showPreview && attachment && attachment.id && (
              <AttachmentContainer onClick={showReader}>
                <Attachment
                  key={attachment.id.toString()}
                  shown
                  pages={1}
                  attachmentId={attachment.id.toString()}
                />
              </AttachmentContainer>
            )}
          </ImpressionWatcher>
        </ContentContainer>

        {work.podcast && (
          <PodcastPlayerWrapper className="podcast-player-wrapper">
            <SimplePaperPodcast
              workId={work.podcast.workId}
              workTitle={work.podcast.workTitle}
              showTitle={false}
              impressionSource="paper_to_podcast_newsfeed"
              onPlayerReady={onPodcastReady}
              showFeedbackSection={false}
              initialEpisode={work.podcast}
            />
          </PodcastPlayerWrapper>
        )}

        {paperInteractions}
      </ContentAndButtonsContainer>
    </>
  );

  return (
    <Container size={size} variant={variant}>
      {content}
    </Container>
  );
};

export default PaperContainer;
